import React, {useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
  GithubOutlined,
  LinkOutlined,
  SecurityScanOutlined
} from '@ant-design/icons';
import styles from '@/styles/pages/projects/CreateProject.module.css';
import {projectsAPI} from "@/api/endpoints/projects.js";

const CreateProject = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    tech_stack: [],
    repository_url: '',
    token: '',
    admin_url: ''
  });

  const [newTech, setNewTech] = useState('');
  const [errors, setErrors] = useState({});

  // Статусы проектов
  const statuses = [
    { value: 'active', label: 'Активный', color: '#3fb950' },
    { value: 'development', label: 'В разработке', color: '#d29922' },
    { value: 'staging', label: 'Тестирование', color: '#8957e5' },
    { value: 'archived', label: 'Архивирован', color: '#7d8590' }
  ];

  useEffect(() => {
      document.title = 'Создание проекта | DevSpace';
  }, []);

  // Валидация URL
  const validateUrl = (url) => {
    if (!url) return true;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  // Обработка изменения полей
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Очищаем ошибку для этого поля
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Валидация URL
    if (name === 'repository_url' && value) {
      if (!validateUrl(value)) {
        setErrors(prev => ({ ...prev, repository_url: 'URL должен начинаться с http:// или https://' }));
      } else {
        setErrors(prev => ({ ...prev, repository_url: null }));
      }
    }
  };

  // Добавление технологии
  const handleAddTech = () => {
    if (newTech.trim() && !formData.tech_stack.includes(newTech.trim())) {
      setFormData(prev => ({
        ...prev,
        tech_stack: [...prev.tech_stack, newTech.trim()]
      }));
      setNewTech('');
    }
  };

  // Удаление технологии
  const handleRemoveTech = (tech) => {
    setFormData(prev => ({
      ...prev,
      tech_stack: prev.tech_stack.filter(t => t !== tech)
    }));
  };

  // Обработка нажатия Enter в поле технологии
  const handleTechKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTech();
    }
  };

  // Валидация формы
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Название проекта обязательно';
    } else if (formData.name.length < 1) {
      newErrors.name = 'Название должно содержать минимум 1 символ';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Название не должно превышать 255 символов';
    }

    if (formData.repository_url && !validateUrl(formData.repository_url)) {
      newErrors.repository_url = 'URL должен начинаться с http:// или https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Отправка формы
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await projectsAPI.create(formData)
      if (response.success) {
        const data = response.data;
        navigate(`/projects/${data.uuid}`);
      } else {
        const error = await response.json();
        setErrors({ submit: error.detail || 'Ошибка при создании проекта' });
      }
    } catch (error) {
      setErrors({ submit: 'Ошибка сети. Попробуйте позже.' });
    } finally {
      setLoading(false);
    }
  };

  // Отмена и возврат назад
  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleCancel}>
          <ArrowLeftOutlined /> Назад
        </button>
        <h1 className={styles.title}>Создание проекта</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Название проекта */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Название проекта <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Введите название проекта"
            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
            maxLength={255}
          />
          {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
          <span className={styles.hint}>
            {formData.name.length}/255 символов
          </span>
        </div>

        {/* Описание проекта */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Описание проекта</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Опишите цели и задачи проекта"
            className={styles.textarea}
            rows={4}
          />
        </div>

        {/* Статус проекта */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Статус проекта</label>
          <div className={styles.statusGroup}>
            {statuses.map(status => (
              <label
                key={status.value}
                className={`${styles.statusOption} ${
                  formData.status === status.value ? styles.statusOptionActive : ''
                }`}
                style={{
                  borderColor: formData.status === status.value ? status.color : 'transparent'
                }}
              >
                <input
                  type="radio"
                  name="status"
                  value={status.value}
                  checked={formData.status === status.value}
                  onChange={handleChange}
                  className={styles.radioInput}
                />
                <span
                  className={styles.statusDot}
                  style={{ backgroundColor: status.color }}
                />
                <span className={styles.statusLabel}>{status.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Стек технологий */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Стек технологий</label>
          <div className={styles.techInputGroup}>
            <input
              type="text"
              value={newTech}
              onChange={(e) => setNewTech(e.target.value)}
              onKeyPress={handleTechKeyPress}
              placeholder="Добавьте технологию (например, React, Python, Docker)"
              className={styles.techInput}
            />
            <button
              type="button"
              onClick={handleAddTech}
              className={styles.addTechButton}
            >
              <PlusCircleOutlined /> Добавить
            </button>
          </div>

          <div className={styles.techStackList}>
            {formData.tech_stack.map((tech, index) => (
              <div key={index} className={styles.techTag}>
                <span className={styles.techTagText}>{tech}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveTech(tech)}
                  className={styles.removeTechButton}
                >
                  <DeleteOutlined />
                </button>
              </div>
            ))}
            {formData.tech_stack.length === 0 && (
              <div className={styles.emptyTechStack}>
                <PlusOutlined /> Добавьте технологии, которые используете в проекте
              </div>
            )}
          </div>
        </div>

        {/* Токен для подключения */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Токен подключения</label>
          <div className={styles.urlInputGroup}>
            <SecurityScanOutlined className={styles.urlIcon} />
            <input
              type="text"
              name="token"
              value={formData.token}
              onChange={handleChange}
              placeholder="Токен"
              className={`${styles.input} ${errors.token ? styles.inputError : ''}`}
            />
          </div>
          {errors.token && (
            <span className={styles.errorMessage}>{errors.token}</span>
          )}
          <span className={styles.hint}>
            Укажите токен подключения к админ панели проекта
          </span>
        </div>

        {/* Ссылка на админку проекта */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Ссылка на админку проекта</label>
          <div className={styles.urlInputGroup}>
            <LinkOutlined className={styles.urlIcon} />
            <input
              type="url"
              name="admin_url"
              value={formData.admin_url}
              onChange={handleChange}
              placeholder="https://some-projects/api/v1/admin/"
              className={`${styles.input} ${errors.admin_url ? styles.inputError : ''}`}
            />
          </div>
          {errors.admin_url && (
            <span className={styles.errorMessage}>{errors.admin_url}</span>
          )}
          <span className={styles.hint}>
            Укажите ссылку на админку проекта
          </span>
        </div>

        {/* Ссылка на репозиторий */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Ссылка на репозиторий</label>
          <div className={styles.urlInputGroup}>
            <GithubOutlined className={styles.urlIcon} />
            <input
              type="url"
              name="repository_url"
              value={formData.repository_url}
              onChange={handleChange}
              placeholder="https://github.com/username/repository"
              className={`${styles.input} ${errors.repository_url ? styles.inputError : ''}`}
            />
          </div>
          {errors.repository_url && (
            <span className={styles.errorMessage}>{errors.repository_url}</span>
          )}
          <span className={styles.hint}>
            Укажите ссылку на GitHub, GitLab или другой репозиторий
          </span>
        </div>

        {/* Ошибка отправки */}
        {errors.submit && (
          <div className={styles.submitError}>
            <CloseOutlined /> {errors.submit}
          </div>
        )}

        {/* Кнопки действий */}
        <div className={styles.actions}>
          <button
            type="button"
            onClick={handleCancel}
            className={styles.cancelButton}
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? (
              <>Создание...</>
            ) : (
              <>
                <CheckOutlined /> Создать проект
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProject;